import { Alert, Divider, Stack, Tab, TabProps, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Control, FieldValues, Path, useForm } from "react-hook-form";
import { ValidationErrors } from "../../graphql/types";
import AccentDanger from "../../icons/AccentDanger.svg?react";
import Verified from "../../icons/Verified.svg?react";
import { COLORS } from "../../styles/sbDesignSystem/sbPalette";
import type { KeyFormValues } from "./KeyEditorTypes";
import {
  SQL_GENERIC_ERRORS,
  SQL_GENERIC_ERRORS_BY_ID,
  SQL_VALIDATION_ERRORS_BY_ID,
} from "./CredentialValidationErrors";
import { FormController } from "./FormController";
import { IFormWrapperProps } from "./FormWrapper";
import { Button, FormSubmitButton } from "./KeyEditorCommon";
import { certificateExample, rsaPrivateKey } from "./KeyInputPlaceholders";
import { PrivateKeyInput } from "./PrivateKeyInput";

type TabWithErrorPropProps = TabProps & { showError?: boolean };

const TabWithErrorProp = ({ showError, ...props }: TabWithErrorPropProps) => (
  <Tab
    {...props}
    icon={
      showError ? (
        <AccentDanger fill={COLORS.accentDanger30.main} color={COLORS.text10.main} width="20px" height="20px" />
      ) : undefined
    }
    iconPosition={showError ? "start" : undefined}
  />
);

type ParsedErrors = {
  parsedGenericErrors: string[];
  parsedSpecificErrors: { [key: string]: string };
};

const parseValidationErrors = (validationErrors?: ValidationErrors): ParsedErrors => {
  const parsedSpecificErrors: { [key: string]: string } = {};
  const parsedGenericErrors: string[] = [];
  if (!validationErrors?.errors) return { parsedGenericErrors, parsedSpecificErrors };
  for (const error of validationErrors.errors) {
    if (!error) continue;
    if (error.typeId in SQL_GENERIC_ERRORS) {
      parsedGenericErrors.push(SQL_GENERIC_ERRORS_BY_ID[error.typeId]);
    } else {
      for (const key of Object.keys(error.properties)) {
        parsedSpecificErrors[key] = SQL_VALIDATION_ERRORS_BY_ID[error.typeId];
      }
    }
  }

  return { parsedGenericErrors, parsedSpecificErrors };
};

enum SQLTab {
  ESSENTIAL_INFORMATION = "essential-information",
  SSH = "ssh",
  SSL = "ssl",
}

type SQLFields = {
  key_name?: string;
  user?: string;
  db_name?: string;
  host?: string;
  dialect?: string;
  port?: string;
  charset?: string;
  password?: string;
  ssh_tunnel_host?: string;
  ssh_tunnel_port?: string;
  ssh_tunnel_user?: string;
  ssh_tunnel_password?: string;
  ssh_tunnel_rsa_key?: string;
  ssh_tunnel_auth_type?: string;
  ssh_tunnel_use_sha1?: boolean;
  client_key_pem?: string;
  client_cert_pem?: string;
  server_ca_pem?: string;
};

const defaultButtonText = "Verify and Save";

const SQLEditor = <T extends FieldValues>(props: IFormWrapperProps<T>) => {
  const {
    credential,
    mode,
    onFormSubmit,
    loading,
    buttonType = "default",
    buttonText = defaultButtonText,
    isOauth,
    onLeave,
    validationErrors,
  } = props;
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    clearErrors,
    formState: { isValid, errors, isSubmitting },
  } = useForm<T | SQLFields>({
    mode: "onChange",
    defaultValues: {
      key_name: credential?.name || "",
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() cannot be memoized; acceptable here as allValues is used only in non-memoized callbacks
  const allValues = watch();

  const [isFormComplete, setIsFormComplete] = useState(false);
  const [authType, setAuthType] = useState(credential?.properties.ssh_tunnel_auth_type || "privateKey");

  const isSectionPartiallyFilled = (fields: (keyof SQLFields)[]) => {
    return fields.some((field) => !!getValues(field));
  };

  const exactlyOneTrue = (arr: boolean[]) => arr.filter(Boolean).length === 1;

  const isSectionComplete = (mandatoryFields: (keyof SQLFields)[], exclusiveFields: (keyof SQLFields)[] = []) => {
    // Returns true if all mandatoryFields are filled, and exactly one of the exclusive fields is true
    return (
      mandatoryFields.every((field) => !!getValues(field)) &&
      (exclusiveFields.length === 0 || exactlyOneTrue(exclusiveFields.map((field) => !!getValues(field))))
    );
  };

  const essentialInformationMandatoryFields: (keyof SQLFields)[] = ["key_name", "user", "db_name", "host", "dialect"];

  if (!credential?.properties_exist["password"]) essentialInformationMandatoryFields.push("password");

  const sshMandatoryCommonFields: (keyof SQLFields)[] = ["ssh_tunnel_host", "ssh_tunnel_user"];
  const sshPasswordOrKeyExists =
    credential?.properties_exist["ssh_tunnel_password"] || credential?.properties_exist["ssh_tunnel_rsa_key"];
  const sshExclusiveFields: (keyof SQLFields)[] = sshPasswordOrKeyExists
    ? []
    : ["ssh_tunnel_password", "ssh_tunnel_rsa_key"];

  const sshMandatoryFields: (keyof SQLFields)[] = sshMandatoryCommonFields.concat(sshExclusiveFields);

  const sslCertsAndKeyExist =
    credential?.properties_exist["client_key_pem"] &&
    credential?.properties_exist["client_cert_pem"] &&
    credential?.properties_exist["server_ca_pem"];

  const sslMandatoryFields: (keyof SQLFields)[] = sslCertsAndKeyExist
    ? []
    : ["client_key_pem", "client_cert_pem", "server_ca_pem"];

  const isEssentialInformationComplete = isSectionComplete(essentialInformationMandatoryFields);

  const isSSHComplete = isSectionComplete(
    sshMandatoryCommonFields,
    sshPasswordOrKeyExists ? undefined : sshExclusiveFields
  );

  const isSSHPartiallyFilled = isSectionPartiallyFilled(sshMandatoryFields);

  const isSSLComplete = sslCertsAndKeyExist || isSectionComplete(sslMandatoryFields);

  const isSSLPartiallyFilled = isSectionPartiallyFilled(sslMandatoryFields);

  useEffect(() => {
    setIsFormComplete(
      isEssentialInformationComplete &&
        (isSSHComplete || !isSSHPartiallyFilled) &&
        (isSSLComplete || !isSSLPartiallyFilled)
    );
  }, [
    allValues,
    isEssentialInformationComplete,
    isSSHComplete,
    isSSHPartiallyFilled,
    isSSLComplete,
    isSSLPartiallyFilled,
  ]);

  const [genericErrors, setGenericErrors] = useState<string[]>([]);

  useEffect(() => {
    const { parsedGenericErrors, parsedSpecificErrors } = parseValidationErrors(validationErrors);

    setGenericErrors(parsedGenericErrors.length > 0 ? parsedGenericErrors : []);

    const parsedErrorsKeys = Object.keys(parsedSpecificErrors);

    if (parsedErrorsKeys.length > 0) {
      clearErrors();
      parsedErrorsKeys.forEach((errorField) => {
        const errorName = errorField === "name" ? "key_name" : errorField;
        setError(errorName as keyof SQLFields, {
          type: "manual",
          message: parsedSpecificErrors[errorField],
        });
      });
    }
  }, [setError, validationErrors, clearErrors]);

  useEffect(() => {
    if (!credential?.properties.ssh_tunnel_auth_type && setValue) {
      setValue("ssh_tunnel_auth_type", "privateKey");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO on verifying: add "Verify and"
  const actualButtonText = buttonText === "Save" && mode === "edit" ? "Save changes" : buttonText;

  const onSubmitHandler = () => {
    const formValues = getValues();

    if (!onFormSubmit) return;
    if (!formValues.ssh_tunnel_password && !formValues?.ssh_tunnel_rsa_key) {
      delete formValues.ssh_tunnel_auth_type;
    }
    if (isOauth) {
      onFormSubmit({ name: formValues?.key_name });
    } else {
      const properties: Record<string, unknown> = {};
      const cred: KeyFormValues = { properties };
      for (const [key, value] of Object.entries(formValues)) {
        if (key === "service_account_key") {
          properties["key_type"] = "json";
        }
        const trimValue = typeof value === "string" ? value.trim() : value;
        if (key === "key_name") {
          cred.name = trimValue;
        } else {
          properties[key] = trimValue;
        }
      }
      onFormSubmit(cred);
    }
  };
  useEffect(() => {
    if (credential) {
      const properties = credential?.properties as T;
      for (const [key, value] of Object.entries(properties)) {
        setValue(key as Path<T>, value);
      }
      if (credential.name) {
        setValue("key_name", credential.name);
      }
    }
  }, [credential, setValue]);
  const [editingField, setEditingField] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState(SQLTab.ESSENTIAL_INFORMATION);
  const controllerAdditionalProps = {
    control: control,
    errors: errors,
    mode: mode,
    setError: setError,
    setValue: setValue,
    credential: credential,
    watch: watch,
    getValues: getValues,
    editingField: editingField,
    setEditingField: setEditingField,
    loading: loading,
  };

  const tabResolver = () => {
    switch (selectedTab) {
      case SQLTab.ESSENTIAL_INFORMATION:
        return (
          <>
            <FormController<T | SQLFields>
              elementType="textField"
              testid="key_name"
              type="text"
              fieldName="key_name"
              label="Key Name"
              rules={{ required: true }}
              control={control}
              errors={errors}
              mode={mode}
              setValue={setValue}
              credential={credential}
              editingField={editingField}
              setEditingField={setEditingField}
              loading={loading}
            />

            <Divider sx={{ gridColumn: "span 2", margin: "12px 0" }} />

            <FormController<T | SQLFields>
              elementType="textField"
              type="text"
              testid="user"
              fieldName="user"
              placeholder="John Smith"
              label="Username"
              rules={{ required: true }}
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="text"
              testid="db_name"
              fieldName="db_name"
              placeholder="my_database"
              label="DB Name"
              rules={{ required: true }}
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="text"
              testid="host"
              fieldName="host"
              label="Host"
              rules={{ required: true }}
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="text"
              testid="dialect"
              fieldName="dialect"
              placeholder="postgres"
              label="SQL Dialect"
              rules={{ required: true }}
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="number"
              testid="port"
              fieldName="port"
              label="Port"
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="text"
              testid="charset"
              fieldName="charset"
              placeholder="utf8"
              label="Charset"
              rules={{ required: false }}
              {...controllerAdditionalProps}
            />

            <FormController<T | SQLFields>
              elementType="textField"
              type="password"
              testid="password"
              fieldName="password"
              label="Password"
              rules={{ required: true }}
              {...controllerAdditionalProps}
            />
          </>
        );
      case SQLTab.SSH:
        return (
          <>
            <Stack sx={{ gap: 2 }}>
              <FormController<T | SQLFields>
                elementType="textField"
                type="text"
                testid="ssh_tunnel_host"
                fieldName="ssh_tunnel_host"
                placeholder="ssh.mycompany.com"
                label="SSH Tunnel Host"
                rules={{ required: false }}
                {...controllerAdditionalProps}
              />
              <FormController<T | SQLFields>
                elementType="textField"
                type="text"
                testid="ssh_tunnel_user"
                fieldName="ssh_tunnel_user"
                placeholder="dave"
                label="SSH Tunnel User"
                rules={{ required: false }}
                {...controllerAdditionalProps}
              />
              <FormController<T | SQLFields>
                elementType="textField"
                type="number"
                testid="ssh_tunnel_port"
                fieldName="ssh_tunnel_port"
                label="SSH Tunnel Port (optional)"
                placeholder="22"
                rules={{ required: false }}
                {...controllerAdditionalProps}
              />
            </Stack>

            <Stack sx={{ gap: 2 }}>
              <PrivateKeyInput
                {...controllerAdditionalProps}
                control={controllerAdditionalProps.control as Control<FieldValues>}
                credential={credential}
                authType={authType}
                setAuthType={setAuthType}
                passwordFieldname="ssh_tunnel_password"
                privateKeyFieldname="ssh_tunnel_rsa_key"
                authTypeFieldname="ssh_tunnel_auth_type"
                useSha1Fieldname="ssh_tunnel_use_sha1"
                passphraseFieldname="ssh_tunnel_passphrase"
                passwordKeyTestid="ssh_tunnel_rsa_key"
                passwordTestid="ssh_tunnel_password"
              />
            </Stack>
          </>
        );
      case SQLTab.SSL:
        return (
          <>
            <FormController<T | SQLFields>
              elementType="textarea"
              type="password"
              testid="client_key_pem"
              fieldName="client_key_pem"
              placeholder={rsaPrivateKey}
              label="Client Key Pem"
              rules={{ required: false }}
              textAreaHeight="72px"
              {...controllerAdditionalProps}
            />
            <FormController<T | SQLFields>
              elementType="textarea"
              type="password"
              testid="client_cert_pem"
              fieldName="client_cert_pem"
              label="Client Cert Pem"
              placeholder={certificateExample}
              rules={{ required: false }}
              textAreaHeight="72px"
              {...controllerAdditionalProps}
            />
            <FormController<T | SQLFields>
              elementType="textarea"
              type="password"
              testid="server_ca_pem"
              fieldName="server_ca_pem"
              placeholder={certificateExample}
              label="Server CA Pem"
              textAreaHeight="72px"
              rules={{ required: false }}
              {...controllerAdditionalProps}
            />
          </>
        );
    }
  };

  const includesErrors = (givenErrors: string[]) => {
    return givenErrors.some((error) => Object.keys(errors).includes(error));
  };

  const hasEssentialInformationValidationErrors = includesErrors([...essentialInformationMandatoryFields, "charset"]);

  const hasSshValidationErrors = includesErrors(sshMandatoryFields);

  const hasSslValidationErrors = includesErrors(sslMandatoryFields);

  const resolveTabIndicatorColor = (tab: SQLTab) => {
    // return red if a tab with errors is opened
    if (
      (tab === SQLTab.ESSENTIAL_INFORMATION && hasEssentialInformationValidationErrors) ||
      (tab === SQLTab.SSH && hasSshValidationErrors) ||
      (tab === SQLTab.SSL && hasSslValidationErrors)
    ) {
      return COLORS.accentDanger30.main;
    }
    return undefined;
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
      <Tabs
        onChange={(_event, newValue) => setSelectedTab(newValue as SQLTab)}
        value={selectedTab}
        centered
        sx={{ minHeight: "fit-content", marginBottom: "40px" }}
        slotProps={{
          indicator: { style: { backgroundColor: resolveTabIndicatorColor(selectedTab) } },
        }}
      >
        <TabWithErrorProp
          label="Essential information"
          value="essential-information"
          showError={hasEssentialInformationValidationErrors}
        />
        <TabWithErrorProp label="SSH (optional)" value="ssh" showError={hasSshValidationErrors} />
        <TabWithErrorProp label="SSL (optional)" value="ssl" showError={hasSslValidationErrors} />
      </Tabs>
      {genericErrors.map((errorMessage, index) => (
        <Alert key={index} severity="error" sx={{ width: "100%" }}>
          <Typography variant="body.medium.regular">{errorMessage}</Typography>
        </Alert>
      ))}
      <Stack sx={{ marginTop: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 48px" }}>
        {tabResolver()}
      </Stack>
      {mode === "edit" || mode === "add" || isOauth ? (
        <Stack direction="row" sx={{ justifyContent: "space-between", marginTop: "64px" }}>
          <Button onClick={onLeave} variant="text" color="secondary">
            <Typography variant="button.large">Cancel</Typography>
          </Button>
          <FormSubmitButton
            isSubmitting={isSubmitting}
            loading={loading || false}
            isValid={isValid && isFormComplete}
            buttonType={buttonType}
            buttonText={actualButtonText}
            endIcon={loading ? undefined : <Verified color={COLORS.text10.main} fill={COLORS.text10.main} />}
            sx={{
              width: mode === "edit" ? "306px" : "224px",
            }}
          />
        </Stack>
      ) : null}
    </form>
  );
};

export { SQLEditor };
